import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { FaqItem } from "../components/landing/FaqItem";
import "../styles/landing.css";
import {
  Thermometer, ClipboardCheck, Package, FileText, ShieldCheck, BellRing,
  Smartphone, Check, ArrowRight, Sparkles, AlertTriangle,
  BookOpenCheck, FileSearch, CalendarClock, Stethoscope, Building2
} from "lucide-react";

/**
 * Landing SEO dedicata: /software-haccp
 * Focus: software HACCP per ristoranti, conformità D.Lgs. 193/2007, controllo temperature,
 * piano pulizie, tracciabilità lotti, ispezioni ASL.
 *
 * Design coerente con LandingPage.js (palette #6C63FF, #1A1A2E, bianchi/lilla, blob).
 */

const TITLE = "Software HACCP per Ristoranti | Yollo — Conforme D.Lgs. 193/2007";
const DESCRIPTION =
  "Yollo è il software HACCP digitale per ristoranti, bar e pizzerie. Temperature, piano pulizie, tracciabilità lotti e schede tecniche da smartphone. Conforme D.Lgs. 193/2007 e Reg. CE 852/2004. Prova gratuita.";

// Iniezione meta tag SEO solo durante la permanenza su questa pagina
const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== "content") el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  if (attrs.content) el.setAttribute("content", attrs.content);
  return { el, created };
};

const useSeo = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    const refs = [
      upsertMeta('meta[name="description"]', { name: "description", content: DESCRIPTION }),
      upsertMeta('meta[property="og:title"]', { property: "og:title", content: TITLE }),
      upsertMeta('meta[property="og:description"]', { property: "og:description", content: DESCRIPTION }),
      upsertMeta('meta[property="og:url"]', { property: "og:url", content: "https://yollo.me/software-haccp" }),
      upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: TITLE }),
      upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: DESCRIPTION }),
    ];
    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://yollo.me/software-haccp");

    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      if (prevCanonical) canonical.setAttribute("href", prevCanonical);
    };
  }, []);
};

// ─── Moduli HACCP ───
const HACCP_MODULES = [
  {
    icon: Thermometer,
    color: "#3B82F6",
    title: "Controllo temperature",
    desc: "Registra le temperature di frigoriferi, celle e abbattitori direttamente dallo smartphone. Alert automatici quando un valore esce dai limiti HACCP.",
    bullets: [
      "Limiti min/max per ogni frigorifero",
      "Notifiche push e Telegram in caso di anomalia",
      "Storico esportabile in PDF per ispezioni ASL",
    ],
  },
  {
    icon: ClipboardCheck,
    color: "#10B981",
    title: "Piano pulizie HACCP",
    desc: "Aree, frequenze (giornaliera / settimanale / mensile), prodotti chimici utilizzati, schede tecniche allegate. Ogni operatore firma digitalmente.",
    bullets: [
      "Checklist con foto di verifica",
      "Assegnazione automatica per turno",
      "Archivio schede tecniche detergenti",
    ],
  },
  {
    icon: Package,
    color: "#F59E0B",
    title: "Tracciabilità lotti",
    desc: "Etichette di tracciabilità su ogni preparato e semilavorato. Allergeni, lotto di origine, data di produzione e scadenza calcolata automaticamente.",
    bullets: [
      "Stampa etichetta in 2 tap",
      "Avvisi prima della scadenza",
      "Storico lotti per richiamo prodotto",
    ],
  },
  {
    icon: FileText,
    color: "#8B5CF6",
    title: "Schede tecniche e fornitori",
    desc: "DDT, schede prodotto, certificati e documenti dei fornitori archiviati in un'unica anagrafica condivisa con cucina e ufficio.",
    bullets: [
      "Auto-fill anagrafica fornitori via P.IVA (VIES)",
      "Allegati a ogni prodotto",
      "Filtri per categoria e allergene",
    ],
  },
  {
    icon: AlertTriangle,
    color: "#EF4444",
    title: "Non conformità e azioni correttive",
    desc: "Apri una segnalazione in tempo reale dalla cucina. Il titolare riceve l'avviso e può assegnare l'azione correttiva con scadenza.",
    bullets: [
      "Allegato foto e nota vocale",
      "Audit trail con data, ora, operatore",
      "Workflow: aperta → in lavorazione → chiusa",
    ],
  },
  {
    icon: BellRing,
    color: "#EC4899",
    title: "Notifiche e calendario",
    desc: "Promemoria automatici per controlli periodici, scadenze documenti, formazione HACCP del personale e rinnovi.",
    bullets: [
      "Calendario condiviso multi-locale",
      "Promemoria via app, email e Telegram",
      "Attestati di formazione archiviati",
    ],
  },
];

// ─── FAQ HACCP ───
const HACCP_FAQ = [
  {
    q: "Yollo è conforme al D.Lgs. 193/2007?",
    a: "Sì. Yollo è progettato sulla normativa HACCP italiana (D.Lgs. 193/2007) e sul Regolamento CE 852/2004. Ogni registrazione è tracciata con data, ora e operatore — esattamente ciò che ASL e NAS richiedono in fase di ispezione.",
  },
  {
    q: "Posso eliminare i registri cartacei?",
    a: "Sì. I dati digitali registrati su Yollo hanno pieno valore documentale ai fini HACCP. Puoi esportare il piano di autocontrollo, il registro temperature e il piano pulizie in PDF firmati digitalmente in qualsiasi momento.",
  },
  {
    q: "Cosa succede se un frigorifero supera la temperatura?",
    a: "Yollo invia immediatamente una notifica push e (se hai l'addon Telegram) un messaggio al titolare. L'operatore deve registrare l'azione correttiva: spostamento prodotti, manutenzione tecnica o eliminazione lotto. Tutto resta tracciato.",
  },
  {
    q: "Quanto tempo serve per attivare il modulo HACCP?",
    a: "Meno di 10 minuti. Aggiungi i frigoriferi (nome, temperatura min/max), le aree di pulizia e gli operatori. Yollo precarica un piano di autocontrollo HACCP standard che puoi personalizzare.",
  },
  {
    q: "Come funziona la tracciabilità dei lotti?",
    a: "Quando carichi una fattura di acquisto, Yollo genera i lotti. Quando produci un piatto, scegli gli ingredienti: il sistema collega il lotto del piatto ai lotti delle materie prime. In caso di richiamo, ricostruisci la filiera in pochi secondi.",
  },
  {
    q: "Funziona offline?",
    a: "L'app è web-based e progettata mobile-first: una volta caricata, gli operatori possono registrare le temperature anche con connessione instabile. I dati vengono sincronizzati appena la connessione torna disponibile.",
  },
];

// ─── Normativa ───
const NORME = [
  { ref: "D.Lgs. 193/2007", desc: "Attuazione direttiva 2004/41/CE — controlli ufficiali sui prodotti alimentari" },
  { ref: "Reg. CE 852/2004", desc: "Igiene dei prodotti alimentari (HACCP obbligatorio per OSA)" },
  { ref: "Reg. CE 178/2002", desc: "Tracciabilità e sicurezza alimentare" },
  { ref: "Reg. UE 1169/2011", desc: "Informazioni al consumatore — allergeni" },
];

const FaqItemLocal = ({ q, a, isOpen, onClick, i }) => (
  <FaqItem question={q} answer={a} isOpen={isOpen} onClick={onClick} index={i} testIdPrefix="haccp-faq-item" />
);

export default function SoftwareHaccp() {
  useSeo();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const [trialInfo, setTrialInfo] = useState({ attivo: false, giorni: 0 });

  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    axios.get(`${API}/superadmin/public-pricing-v2`)
      .then((res) => {
        setTrialInfo({
          attivo: res.data.trial?.attivo || false,
          giorni: res.data.trial?.giorni || 0,
        });
      })
      .catch(() => {});
  }, []);

  const hasTrial = trialInfo.attivo && trialInfo.giorni > 0;
  const ctaPrimary = hasTrial ? "Prova Gratis" : "Inizia subito";
  const ctaHero = hasTrial ? "Inizia gratis" : "Inizia subito";
  const ctaBottom = hasTrial ? "Inizia ora gratis" : "Inizia subito";

  return (
    <div className="min-h-screen overflow-x-hidden relative" style={{ background: "#FAFAFE" }} data-testid="software-haccp-page">
      {/* Background blobs */}
      <div className="fixed inset-0 -z-0 overflow-hidden pointer-events-none">
        <div className="lp-blob lp-blob-1" />
        <div className="lp-blob lp-blob-2" />
        <div className="lp-blob lp-blob-3" />
      </div>

      {/* ═══════ HEADER ═══════ */}
      <header className="relative z-50 bg-white/85 backdrop-blur-xl border-b border-gray-200/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="haccp-logo-home">
            <img src="/yollo-logo.png" alt="Yollo — Software HACCP" className="w-11 h-11 rounded-xl" />
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/login")} className="text-gray-600 font-semibold rounded-xl" data-testid="haccp-header-login-btn">
              Accedi
            </Button>
            <Button
              onClick={() => navigate("/register")}
              className="rounded-xl font-bold text-white px-5 bg-[#1A1A2E] hover:bg-[#6C63FF] transition-all duration-300"
              data-testid="haccp-header-register-btn"
            >
              {ctaPrimary}
            </Button>
          </div>
        </div>
      </header>

      {/* ═══════ HERO ═══════ */}
      <section className="relative pt-16 pb-16 sm:pt-24 sm:pb-24 overflow-hidden">
        <div className="absolute top-10 left-1/4 w-[500px] h-[500px] rounded-full opacity-30 pointer-events-none animate-blob" style={{ background: "radial-gradient(circle, #6C63FF30, transparent 70%)" }} />
        <div className="absolute top-40 right-1/4 w-[400px] h-[400px] rounded-full opacity-20 pointer-events-none animate-blob animation-delay-2000" style={{ background: "radial-gradient(circle, #10B98130, transparent 70%)" }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <Link to="/" className="text-sm text-gray-500 hover:text-[#6C63FF] mb-6 inline-block" data-testid="haccp-back-link">
            ← Torna a Yollo
          </Link>
          <div className="inline-flex items-center gap-2 bg-white border border-[#6C63FF]/20 rounded-full px-4 py-1.5 text-xs font-bold mb-6 shadow-sm" style={{ color: "#6C63FF" }}>
            <ShieldCheck className="w-3.5 h-3.5" /> Conforme D.Lgs. 193/2007 · Reg. CE 852/2004
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]" style={{ color: "#1A1A2E" }}>
            Il <span className="hero-gradient-text">software HACCP</span><br />
            che i ristoratori usano davvero.
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
            Temperature, piano pulizie, tracciabilità lotti e schede tecniche — tutto da smartphone,
            in regola con la normativa italiana. Niente più carta, niente più ispezioni con il cuore in gola.
          </p>
          <div className="mt-10 flex flex-col items-center gap-3">
            <Button
              onClick={() => navigate("/register")}
              className="h-14 px-8 text-base font-bold rounded-2xl text-white bg-[#1A1A2E] hover:bg-[#6C63FF] transition-all duration-300 hover:scale-105 active:scale-95 shadow-[0_4px_20px_rgba(26,26,46,0.25)] hover:shadow-[0_4px_20px_rgba(108,99,255,0.35)]"
              data-testid="haccp-hero-cta-btn"
            >
              {ctaHero} <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            {hasTrial && (
              <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold">
                <Sparkles className="h-4 w-4" /> Prova gratuita · senza carta richiesta in attivazione
              </div>
            )}
          </div>

          {/* KPI Bar */}
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { v: "< 10'", l: "Setup HACCP" },
              { v: "100%", l: "Da smartphone" },
              { v: "PDF", l: "Export ispezione ASL" },
              { v: "24/7", l: "Alert temperature" },
            ].map((k, i) => (
              <div key={i} className="bg-white border border-gray-200/60 rounded-2xl p-4 shadow-sm" data-testid={`haccp-kpi-${i}`}>
                <div className="text-2xl sm:text-3xl font-black tracking-tight" style={{ color: "#6C63FF" }}>{k.v}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">{k.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ MODULI HACCP ═══════ */}
      <section className="py-20 sm:py-24" id="moduli-haccp">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ color: "#6C63FF", backgroundColor: "#6C63FF15" }}>I moduli HACCP</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
              Sei strumenti, un unico piano<br />di autocontrollo digitale.
            </h2>
            <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
              Pensati con ristoratori veri, testati ogni giorno in cucine reali.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {HACCP_MODULES.map((m, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 border border-gray-200/60 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                data-testid={`haccp-module-${i}`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${m.color}15` }}>
                  <m.icon className="w-6 h-6" style={{ color: m.color }} />
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">{m.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">{m.desc}</p>
                <ul className="space-y-2">
                  {m.bullets.map((b, bi) => (
                    <li key={bi} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: m.color }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ COME FUNZIONA (HowTo) ═══════ */}
      <section className="py-20 sm:py-24" style={{ background: "linear-gradient(180deg, #F0EFFE, #FAFAFE)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ color: "#10B981", backgroundColor: "#10B98115" }}>Come funziona</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
              Dal registro cartaceo al digitale<br />in 5 passi.
            </h2>
          </div>

          <div className="space-y-4">
            {[
              { icon: Building2, t: "Registra il tuo locale", d: "Crea l'account, inserisci nome, indirizzo e P.IVA. Yollo precompila l'anagrafica fornitori dal VIES." },
              { icon: Thermometer, t: "Aggiungi frigoriferi e celle", d: "Definisci limiti minimi e massimi per ogni punto critico (CCP). Lo standard è precaricato." },
              { icon: ClipboardCheck, t: "Imposta il piano pulizie", d: "Aree, frequenze, prodotti chimici, schede tecniche. Assegni i task agli operatori in pochi click." },
              { icon: Smartphone, t: "Gli operatori registrano dal telefono", d: "Temperature, pulizie firmate, lotti etichettati: tutto in 30 secondi, anche con le mani occupate." },
              { icon: FileSearch, t: "Esporta in PDF quando arriva l'ASL", d: "Storico completo, registro temperature, piano pulizie firmato e non conformità: tutto in un PDF pronto." },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-5 bg-white rounded-2xl p-5 border border-gray-200/60 shadow-sm" data-testid={`haccp-step-${i}`}>
                <div className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-lg" style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)" }}>
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <s.icon className="w-4 h-4 text-[#6C63FF]" />
                    <h3 className="font-bold text-gray-900">{s.t}</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ NORMATIVA / CONFORMITÀ ═══════ */}
      <section className="py-20 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ color: "#EF4444", backgroundColor: "#EF444415" }}>Conformità normativa</span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-black text-gray-900 tracking-tight leading-tight">
                In regola con ASL, NAS<br />e Ministero della Salute.
              </h2>
              <p className="mt-4 text-gray-600 leading-relaxed">
                Yollo è progettato sulle normative italiane ed europee in materia di sicurezza alimentare.
                Ogni registrazione è marcata temporalmente, firmata dall'operatore e archiviata in modo immutabile.
                Quando arriva un controllo, hai tutto pronto.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#6C63FF]">
                <Stethoscope className="w-4 h-4" /> Audit trail conforme alle linee guida ministeriali
              </div>
            </div>

            <div className="space-y-3">
              {NORME.map((n, i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-gray-200/60 shadow-sm flex items-start gap-3" data-testid={`haccp-norma-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#6C63FF]/10 flex items-center justify-center flex-shrink-0">
                    <BookOpenCheck className="w-5 h-5 text-[#6C63FF]" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">{n.ref}</div>
                    <div className="text-xs text-gray-600 mt-0.5">{n.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ A CHI SERVE ═══════ */}
      <section className="py-16 sm:py-20" style={{ background: "linear-gradient(180deg, #FAFAFE, #F0EFFE)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ color: "#F59E0B", backgroundColor: "#F59E0B15" }}>A chi serve</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
              Tutti gli OSA (Operatori del Settore Alimentare)
            </h2>
            <p className="mt-3 text-gray-600">Se prepari, somministri o vendi alimenti, l'HACCP è obbligatorio. Yollo è pensato per:</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {["Ristoranti", "Pizzerie", "Bar e caffetterie", "Pasticcerie", "Gelaterie", "Trattorie", "Catering", "Mense aziendali", "Dark kitchen", "Hotel ristorazione", "Birrerie", "Food truck"].map((cat, i) => (
              <div key={i} className="bg-white rounded-xl px-4 py-3 border border-gray-200/60 shadow-sm text-center" data-testid={`haccp-target-${i}`}>
                <span className="text-sm font-semibold text-gray-800">{cat}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FAQ ═══════ */}
      <section id="faq-haccp" className="py-20 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ color: "#6C63FF", backgroundColor: "#6C63FF15" }}>FAQ HACCP</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">Domande frequenti</h2>
          </div>

          <div className="bg-white rounded-[24px] border border-gray-200/60 shadow-md px-6 sm:px-8" data-testid="haccp-faq-section">
            {HACCP_FAQ.map((f, i) => (
              <FaqItemLocal
                key={i}
                i={i}
                q={f.q}
                a={f.a}
                isOpen={openFaq === i}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ CTA FINALE ═══════ */}
      <section className="py-20 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="rounded-[28px] p-8 sm:p-12 text-center text-white" style={{ background: "linear-gradient(135deg, #1F1B3D, #6C63FF)" }}>
            <CalendarClock className="w-10 h-10 mx-auto mb-4 opacity-80" />
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight">
              Quanto tempo passi ogni sera<br />a compilare i registri?
            </h2>
            <p className="mt-4 opacity-90 max-w-xl mx-auto">
              Con Yollo, l'HACCP si chiude in 30 secondi a fine servizio.{" "}
              {hasTrial
                ? "Provalo gratis, senza carta di credito in fase di attivazione."
                : "Attivalo in pochi minuti e parti subito."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Button
                onClick={() => navigate("/register")}
                className="h-14 px-8 text-base font-bold rounded-2xl text-[#1F1B3D] bg-white hover:bg-[#F5F4FE] transition-all duration-300 hover:scale-105"
                data-testid="haccp-cta-bottom-btn"
              >
                {ctaBottom} <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="h-14 px-8 text-base font-bold rounded-2xl text-white border-2 border-white bg-transparent hover:bg-white hover:text-[#1F1B3D] transition-all duration-300"
                data-testid="haccp-cta-explore-btn"
              >
                Scopri tutto Yollo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer className="border-t border-gray-200 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/yollo-logo.png" alt="Yollo" className="w-10 h-10 rounded-lg" />
            <span className="text-sm text-gray-500">Yollo — Software HACCP per ristoranti italiani</span>
          </div>
          <div className="flex items-center gap-4 text-sm flex-wrap justify-center">
            <Link to="/privacy" className="text-gray-400 hover:text-gray-600">Privacy Policy</Link>
            <Link to="/cookie-policy" className="text-gray-400 hover:text-gray-600">Cookie Policy</Link>
            <span className="text-gray-300 hidden sm:inline">|</span>
            <button onClick={() => navigate("/login")} className="text-gray-500 hover:text-gray-700 font-medium" data-testid="haccp-footer-login-link">Accedi</button>
            <button onClick={() => navigate("/register")} className="font-bold hover:underline" style={{ color: "#6C63FF" }} data-testid="haccp-footer-register-link">Registrati</button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">© 2026 Luchapp S.r.l. — Via Tucci 4, 04100 Latina (LT) — P.IVA 03368240598</p>
        </div>
      </footer>
    </div>
  );
}
